import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../utils/tenantApi';
import { useAuth } from '../context/AuthContext';

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Australia/Melbourne',
  }) + ' AEST';
}

export default function TenantSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]                     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [withdrawing, setWithdrawing]       = useState(false);
  const [successBanner, setSuccessBanner]   = useState('');

  useEffect(() => {
    API.get('/tenant/portal')
      .then(({ data }) => setData(data))
      .catch(() => navigate('/tenant/portal', { replace: true }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await API.post('/tenant/consent/withdraw');
      // Reload portal data
      const { data: fresh } = await API.get('/tenant/portal');
      setData(fresh);
      setShowWithdrawConfirm(false);
      setSuccessBanner('Your consent has been withdrawn. Your landlord has been notified.');
    } catch (err) {
      setShowWithdrawConfirm(false);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  const tenant = data?.tenant || {};
  const consentGiven     = tenant.consentGiven;
  const consentWithdrawn = tenant.consentWithdrawn;
  const consentActive    = consentGiven && !consentWithdrawn;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <Link to="/tenant/portal" className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-brand-600 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-slate-800">RentFriendly</span>
          </div>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-600">Preferences</span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8 space-y-4">
        {/* Success banner */}
        {successBanner && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm text-green-800">{successBanner}</p>
          </div>
        )}

        {/* Consent card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-slate-800 mb-1">Electronic communication</h2>
          <p className="text-xs text-slate-400 mb-5">
            Whether your landlord can send you formal notices by email
          </p>

          {/* Status badge */}
          {consentActive ? (
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Consented
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                Not consented
              </span>
            </div>
          )}

          {consentActive ? (
            <>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                You have agreed to receive rental notices and documents from your landlord
                electronically at <span className="font-medium text-slate-800">{tenant.email}</span>.
              </p>
              {tenant.consentDate && (
                <p className="text-xs text-slate-400 mb-4">
                  Consented on {fmtDateTime(tenant.consentDate)}
                </p>
              )}
              <p className="text-sm text-slate-500 mb-5">
                You can withdraw this consent at any time. If you withdraw, your landlord must
                deliver all formal notices by hand or post.
              </p>
              <button
                onClick={() => setShowWithdrawConfirm(true)}
                className="text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2.5 rounded-xl transition-colors">
                Withdraw consent
              </button>
            </>
          ) : consentWithdrawn ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                You previously consented but have since withdrawn. Your landlord must deliver all
                formal notices by hand or post.
              </p>
              {tenant.consentWithdrawnDate && (
                <p className="text-xs text-slate-400">Withdrawn on {fmtDateTime(tenant.consentWithdrawnDate)}</p>
              )}
            </div>
          ) : !tenant.consentDate ? (
            <div>
              <p className="text-sm text-slate-600 mb-4">
                You haven't yet responded to the electronic consent request. Your landlord must
                currently deliver formal notices by hand or post.
              </p>
              <Link
                to="/tenant/consent"
                className="inline-block text-sm bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-xl font-medium transition-colors">
                Review consent request
              </Link>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              You declined electronic notices. Your landlord must deliver formal notices by hand or post.
            </p>
          )}
        </div>

        {/* Account info */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 mb-3">Account</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Name</p>
              <p className="text-sm text-slate-800">{tenant.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Email</p>
              <p className="text-sm text-slate-800">{tenant.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw confirmation dialog */}
      {showWithdrawConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowWithdrawConfirm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <h2 className="text-base font-semibold text-slate-800 mb-3">Withdraw consent?</h2>
              <p className="text-sm text-slate-600 mb-2">
                Your landlord will be notified and must deliver all future formal notices by hand or post.
              </p>
              <p className="text-sm text-slate-500 mb-5">
                Your original consent record is kept for legal purposes.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowWithdrawConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">
                  Cancel
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-xl text-sm font-semibold min-h-[44px]">
                  {withdrawing ? 'Withdrawing…' : 'Withdraw consent'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
